import { getCarsFx } from 'api/cars'
import { URL_PARSE_OPTIONS, URL_STRINGIFY_OPTIONS } from 'application/configuration/constants'
import { AppTheme as InternalAppTheme } from 'application/theme'
import { createEffect, createEvent, createStore, restore } from 'effector'
import { createGate } from 'effector-react'
import { attach } from 'effector/effector.umd'
import { CarData } from 'features/car/types'
import { CarsListProps, Order, Sort, Sorting } from 'features/cars-list/types'
import { $filterValues } from 'features/filter/model'
import { appendUrl } from 'lib/append-url'
import { parseUrl } from 'query-string'

export const DEFAULT_SORTING: { sort: Sort | null; order: Order | null } = {
  sort: null,
  order: null
}

/**
 * Идёт ли загрузка машин в данный момент
 */
export const $fetching = createStore(true)

/**
 * Сколько страниц было загружено
 */
export const $pagesLoaded = createStore<number>(1).reset($filterValues)

/**
 * Общее количество машин по фильтру
 */
export const $totalCount = createStore<number>(0)

/**
 * Стор автомобилей, полученых по запросу с бэка
 */
export const $cars = createStore<CarData[] | null>(null)

/**
 * Гейт списка автомобилей, просит StageProps всего стейджа и тему приложения (требуется для нормализации данных)
 */
export const $carsStageGate = createGate<{ carsListProps: CarsListProps; appTheme: InternalAppTheme }>()

/**
 * Загрузить следующую страницу (подгрузить ещё машины)
 */
export const loadNextPage = createEffect()

/**
 * Сортировка списка автомобилей
 */
export const $sorting = createStore<{ sort: Sort | null; order: Order | null }>(DEFAULT_SORTING)

/**
 * Неудачная загрузка списка
 */
export const $failed = createStore(false)

/**
 * Применить следующий вариант сортировки для ключа
 */
export const nextSort = createEvent<Sort>()

/**
 * Применить следующий вариант сортировки для ключа
 */
export const setSort = createEvent<Sort>()

/**
 * Эффект, возвращающий из УРЛ параметры сортировки
 */
export const getSortingFromUrlFx = createEffect(async (initialSorting?: Sorting['options'][number]) => {
  const urlQuery = parseUrl(window.location.href, URL_PARSE_OPTIONS).query

  if (urlQuery.sort && urlQuery.order) {
    return {
      sort: urlQuery.sort as Sort,
      order: urlQuery.order as Order
    }
  }

  if (initialSorting) {
    return {
      sort: initialSorting.key,
      order: initialSorting.order || DEFAULT_SORTING.order
    }
  }

  return DEFAULT_SORTING
})

/**
 * Эффект, возвращающий из УРЛ текущую страницу
 */
export const getPageFromURLFx = createEffect(async () => {
  const urlQuery = parseUrl(window.location.href, URL_PARSE_OPTIONS).query
  return urlQuery.page && typeof urlQuery.page === 'number' && urlQuery.page > 1 ? urlQuery.page : 1
})

/**
 * Эффект, отправляющий данные о сортировке и загруженой странице в URL
 */
export const propagateToUrlFx = createEffect(
  ({ sorting, pagesLoaded }: { pagesLoaded: number; sorting: { sort: Sort | null; order: Order | null } | null }) => {
    const newUrl = appendUrl({
      url: window.location.href,
      parseOptions: URL_PARSE_OPTIONS,
      stringifyOptions: URL_STRINGIFY_OPTIONS,
      data: {
        sort: sorting?.sort || null,
        order: sorting?.sort ? sorting.order : null,
        page: pagesLoaded > 1 ? pagesLoaded : null
      }
    })
    window.history.replaceState({}, '', newUrl)
  }
)

/**
 * Эффект запроса машин. Требует множество параметров, которые потом будут использованы в запросе -
 * страница, фильтр, сортировка, параметры этажа и тему приложения (для нормализации данных которые придут) итд
 */

export const fetchCarsOnListFx = attach({
  effect: getCarsFx
})

export const $maxPrice = restore(
  fetchCarsOnListFx.doneData.map((data) => data.maxPrice),
  null
)
export const $minPrice = restore(
  fetchCarsOnListFx.doneData.map((data) => data.minPrice),
  null
)

export const $firstLoadPage = createStore(true)
export const setFirstLoadPage = createEvent<boolean>()

export const $filterWithDefaultValue = createStore(false)
export const setFilterWithDefaultValue = createEvent<boolean>()
